/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import type AssessmentType from '@linc-technologies/linced-common/enums/assessment-type';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Category from 'linced-admin/models/assessments/category';
import type GradeSchedule from 'linced-admin/models/assessments/grade-schedule';
import type Module from 'linced-admin/models/assessments/module';
import type UsageSet from 'linced-admin/models/schools/usage-set';

// Proto: assessments.AssessmentTemplate.
export default class Assessment extends Model {
  declare [Type]: 'assessments/assessment';

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isPublished: boolean;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @belongsTo('assessments/category', { async: true, inverse: 'assessments' })
  declare categoryId: AsyncBelongsTo<Category>;

  @alias('categoryId')
  declare category: AsyncBelongsTo<Category>;

  @alias('category.id')
  declare category_id: string | null | undefined;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  // Usage sets membership
  @hasMany('schools/usage-set', {
    async: true,
    inverse: 'assessmentTemplateIds',
  })
  declare usageSetIds: AsyncHasMany<UsageSet>;

  @computed('usageSetIds.content.[]')
  get usageSetsArray() {
    return resolved(this.usageSetIds);
  }

  @hasMany('assessments/module', { async: true, inverse: 'assessmentId' })
  declare modules: AsyncHasMany<Module>;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof AssessmentType> | 0;

  @attr('number', { defaultValue: 0 })
  declare assessmentDisplay: EnumValue<typeof AssessmentDisplay> | 0;

  @attr('string')
  declare key: string | undefined;

  @belongsTo('assessments/grade-schedule', { async: true, inverse: null })
  declare gradeScheduleId: AsyncBelongsTo<GradeSchedule>;

  @alias('gradeScheduleId')
  declare gradeSchedule: AsyncBelongsTo<GradeSchedule>;

  @attr('boolean', { defaultValue: false })
  declare isModerated: boolean;

  @attr('boolean', { defaultValue: false })
  declare sharesEvidence: boolean;

  @attr('number', { defaultValue: 0 })
  declare numericStepping: number;

  @attr('number', { defaultValue: 0 })
  declare dueTime: number;

  @attr('boolean', { defaultValue: false })
  declare isVisibleToLearners: boolean;

  @attr('boolean', { defaultValue: false })
  declare canLearnersEdit: boolean;

  @attr('boolean', { defaultValue: false })
  declare showHistoryToLearners: boolean;

  @attr('number', { defaultValue: 0 })
  declare noGradesColumnsToShowCaregiversStudents: number;

  @attr('number', { defaultValue: 0 })
  declare noGradesColumnsToShowStaff: number;

  @attr('boolean', { defaultValue: false })
  declare showToCaregiversOnlyIfContent: boolean;
}
